import React, { useState, useEffect, useCallback } from 'react';
import { FileText, RefreshCw, AlertTriangle, Info } from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const fmt = (v) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Renders one section-grouped block (top labor distribution, or bottom tax/payroll).
const SectionTable = ({ block, totalLabel, totalValue }) => {
  const sections = block?.sections || [];
  const cols = block?.columns || [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100 text-left">
            {cols.map((c, i) => (
              <th key={i} className={`px-3 py-2 font-semibold border-b ${i === 2 ? 'text-right' : ''}`}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((sec, si) => (
            <React.Fragment key={si}>
              {sec.group && sec.group !== 'Ungrouped' && (
                <tr className="bg-blue-50/60">
                  <td className="px-3 py-1.5 font-semibold text-blue-900" colSpan={cols.length}>
                    {sec.group}
                  </td>
                </tr>
              )}
              {sec.rows.map((row) => (
                <tr key={row.rowId} className="border-b hover:bg-blue-50/40">
                  <td className="px-3 py-1.5 font-mono text-gray-700">{row.code}</td>
                  <td className="px-3 py-1.5">{row.description}</td>
                  <td className="px-3 py-1.5 text-right">{fmt(row.amount)}</td>
                  <td className="px-3 py-1.5 text-gray-500">{row.reference}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold border-b-2 border-gray-300">
                <td className="px-3 py-1.5" />
                <td className="px-3 py-1.5">Subtotal{sec.group && sec.group !== 'Ungrouped' ? ` — ${sec.group}` : ''}</td>
                <td className="px-3 py-1.5 text-right">{fmt(sec.subtotal)}</td>
                <td className="px-3 py-1.5" />
              </tr>
            </React.Fragment>
          ))}
          <tr className="bg-gray-900 text-white font-bold">
            <td className="px-3 py-2" />
            <td className="px-3 py-2">{totalLabel}</td>
            <td className="px-3 py-2 text-right">{fmt(totalValue)}</td>
            <td className="px-3 py-2" />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const ScheduleLPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/schedule-l/${clientId}`);
      if (!res.ok) throw new Error(`Failed to load Schedule L (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!hasActiveClient) {
    return (
      <div className="p-6">
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>Select a client to view Schedule L.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold">{data?.title || 'Schedule L'}</h1>
            <p className="text-sm text-gray-500">{clientName}</p>
          </div>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <Alert className="mb-4">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {data && !data.configured && (
        <Alert className="mb-4">
          <Info className="w-4 h-4" />
          <AlertDescription>
            Schedule L is not configured yet. Upload the account listing and the tax/payroll block in
            the onboarding wizard (Schedule Configuration → Schedule L Listing).
          </AlertDescription>
        </Alert>
      )}

      {data?.configured && data?.config_status && data.config_status !== 'active' && (
        <Alert className="mb-4">
          <Info className="w-4 h-4" />
          <AlertDescription>
            Preview from a draft configuration. Activate the client config in the onboarding wizard to finalize.
          </AlertDescription>
        </Alert>
      )}

      {data?.configured && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Total Labor Distribution (from GL)</CardTitle>
            </CardHeader>
            <CardContent>
              {data.labor_distribution?.amounts_pending && (
                <Alert className="mb-3">
                  <Info className="w-4 h-4" />
                  <AlertDescription>GL amounts are populated in the formatting pass.</AlertDescription>
                </Alert>
              )}
              <SectionTable
                block={data.labor_distribution}
                totalLabel="TOTAL LABOR DISTRIBUTION"
                totalValue={data.labor_distribution?.total_labor_distribution}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tax / Payroll Reconciliation (entered — 941 + ADP)</CardTitle>
            </CardHeader>
            <CardContent>
              <SectionTable
                block={data.tax_payroll}
                totalLabel="TOTAL PAYROLL"
                totalValue={data.tax_payroll?.total_payroll}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-lg">Net Difference</span>
                <span
                  className={`font-bold text-lg ${
                    data.net_difference && Math.abs(data.net_difference) > 0.5 ? 'text-red-600' : 'text-green-700'
                  }`}
                >
                  {fmt(data.net_difference)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Total Labor Distribution − Total Payroll. Should net to ~0 once all amounts are wired.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ScheduleLPage;
